import { useEffect, useRef, useState } from "react";
import {
  Apple,
  ArrowDown,
  Check,
  ChevronDown,
  Copy,
  Eraser,
  ImagePlus,
  Keyboard,
  MousePointer2,
  Palette,
  Save,
  ScanLine,
  Sparkles,
  Sun,
  Terminal,
  Type,
  Layers,
  Share2,
  Lock,
  RotateCw,
  Pen,
Wand2,
  Shapes,
  Network,
  ListTree,
  FileText,
  Move,
  MoveRight,
  X,
} from "lucide-react";
import "./index.css";

import icon from "./macdraw.png";

const features = [
  {
    icon: Shapes,
    title: "40+ shape library",
    desc: "Polygons, flowchart, architecture and communication shapes — plus data structures: linked list, stack, heap, graph and set, each with editable node values.",
  },
  {
    icon: Network,
    title: "Data-structure shapes",
    desc: "Draw a linked list, stack, heap, graph or Venn set and double-click any node to type its value — perfect for teaching CS on screen.",
  },
  {
    icon: MoveRight,
    title: "Smart connectors",
    desc: "Arrows and connectors auto-route around shapes with clean right-angle bends and perpendicular edge connections.",
  },
  {
    icon: MousePointer2,
    title: "21 one-key tools",
    desc: "Rectangle, diamond, ellipse, arrow, freehand sketch, magic shapes, frames and more — each tool is a keystroke away.",
  },
  {
    icon: ScanLine,
    title: "Glowing laser pointer",
    desc: "A neon laser that fades tail-first and never leaves marks. Perfect for presentations.",
  },
  {
    icon: Sun,
    title: "White or black screen",
    desc: "Flip the whole screen to a clean white or black writing surface with one click.",
  },
  {
    icon: Palette,
    title: "Full color palette",
    desc: "Stroke and fill colors across curated families, plus adjustable stroke width and opacity.",
  },
  {
    icon: Type,
    title: "Real text & fonts",
    desc: "Type anywhere on screen, choose your font family and size, and re-edit later.",
  },
  {
    icon: FileText,
    title: "Markdown text",
    desc: "Type # headings, - bullets, **bold**, *italic*, ```code blocks and more — your notes render as a clean document.",
  },
  {
    icon: ListTree,
    title: "Multiple pages",
    desc: "Keep separate canvases per project or class — name them, add a note, and switch in one click.",
  },
  {
    icon: ImagePlus,
    title: "Images & emoji",
    desc: "Drop pictures onto the screen, or press / for a quick emoji and symbol palette.",
  },
  {
    icon: Save,
    title: "Autosaves everything",
    desc: "Your drawing persists across quits — close the app, come back, keep drawing.",
  },
  {
    icon: Eraser,
    title: "Eraser, lasso & fill",
    desc: "Erase strokes, lasso-select groups, move/resize/rotate, bucket-fill closed shapes.",
  },
  {
    icon: Keyboard,
    title: "Keyboard-first",
    desc: "V select, R rect, O ellipse, A arrow, T text… every tool is a keystroke away.",
  },
  {
    icon: Layers,
    title: "Layer management",
    desc: "Bring to front, send to back, and organize your annotations with layer controls.",
  },
  {
    icon: Share2,
    title: "Export & share",
    desc: "Export your drawings as PNG, SVG, or PDF for easy sharing and collaboration.",
  },
  {
    icon: Lock,
    title: "Lock annotations",
    desc: "Lock specific annotations to prevent accidental edits while you work on others.",
  },
  {
    icon: Pen,
    title: "Trackpad signature",
    desc: "Pressure-sensitive drawing on trackpad — just like a signature pad for natural handwriting.",
  },
  {
    icon: Move,
    title: "Infinite canvas",
    desc: "Pan anywhere with space + drag or a two-finger scroll, and pinch to zoom 15%–800%. Run out of room? Never again.",
  },
  {
    icon: Wand2,
    title: "Auto-fill shapes",
    desc: "Draw a closed shape and it automatically fills when complete — perfect for quick diagrams.",
  },
];

const shortcuts: [string, string][] = [
  ["Select", "V"],
  ["Pan", "H"],
  ["Pan canvas", "Space + drag"],
  ["Zoom canvas", "Pinch / ⌘scroll"],
  ["Reset view", "⌘0"],
  ["Rectangle", "R"],
  ["Diamond", "Y"],
  ["Ellipse", "O"],
  ["Arrow", "A"],
  ["Line", "L"],
  ["Sketch", "D"],
  ["Text", "T"],
  ["Image", "I"],
  ["Eraser", "E"],
  ["Frame", "F"],
  ["Magic shape", "G"],
  ["Lasso", "S"],
  ["Laser", "Z"],
  ["Fill", "B"],
  ["Undo", "⌘Z"],
  ["Select all", "⌘A"],
  ["Delete selection", "⌫"],
  ["Close overlay", "⌃⌥"],
];

const apps = [
  "Keynote",
  "Zoom",
  "Chrome",
  "Safari",
  "Figma",
  "Slack",
  "Xcode",
  "PowerPoint",
  "VS Code",
  "Notion",
  "Discord",
  "OBS",
];

const faqs: [string, string][] = [
  [
    "Is it really free?",
    "Yes — free forever, no account, no ads, no telemetry. The whole app and site are public on GitHub, so anyone can read (or build) what's inside.",
  ],
  [
    "Why does macOS show a warning when I open it?",
    "Apple only trusts apps notarized under its paid developer program. macdraw is a free, self-signed app, so macOS asks once. Right-click the app, choose Open, then Open again — every launch after that is normal. Terminal and AirDrop installs skip the prompt entirely.",
  ],
  [
    "Will it slow down my Mac?",
    "No. It's a ~1 MB AppKit app that idles at near-zero CPU and only wakes when you draw. The overlay sits at the highest window level, so your apps keep running underneath untouched.",
  ],
  [
    "Does it work on multiple monitors?",
    "Yes — the overlay and toolbar appear on every connected display, and your drawing spans across them.",
  ],
  [
    "How do I save or share my drawing?",
    "Everything autosaves to your Mac the moment you draw — close the app and come back later to keep going. Export as PNG, SVG, or PDF for easy sharing.",
  ],
  [
    "How do I close the overlay?",
    "Press ⌃⌥ (Control + Option) — the same shortcut that opens it. Drawings stay, the overlay disappears.",
  ],
  [
    "Can I use it for presentations?",
    "Absolutely! The laser pointer tool is perfect for presentations, and you can draw over any app including Keynote, PowerPoint, or Zoom.",
  ],
  [
    "What's the difference from similar tools?",
    "macdraw is lightweight, keyboard-first, and designed specifically for macOS. It doesn't require signups, works offline, and costs nothing.",
  ],
];

function InteractiveDemo() {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [demoShapes, setDemoShapes] = useState<Array<{id: string, type: string, x: number, y: number, color: string}>>([]);
  
  const tools = [
    { id: 'rect', name: 'Rectangle', key: 'R', icon: '▢' },
    { id: 'ellipse', name: 'Ellipse', key: 'O', icon: '○' },
    { id: 'arrow', name: 'Arrow', key: 'A', icon: '→' },
    { id: 'text', name: 'Text', key: 'T', icon: 'T' },
    { id: 'laser', name: 'Laser', key: 'Z', icon: '✦' },
  ];

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!activeTool) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    const newShape = {
      id: Date.now().toString(),
      type: activeTool,
      x,
      y,
      color: randomColor
    };
    
    setDemoShapes([...demoShapes, newShape]);
    setActiveTool(null);
  };

  const resetDemo = () => {
    setDemoShapes([]);
    setActiveTool(null);
  };

  return (
    <section className="w-full py-20 bg-gradient-to-b from-neutral-50 to-white">
      <div className="mx-auto max-w-4xl px-6">
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">
              Try it right here
            </h2>
            <p className="mt-4 text-lg text-neutral-600">
              Select a tool and click on the canvas to see macdraw in action
            </p>
          </div>
        </Reveal>
        
        <Reveal d={100}>
          <div className="relative">
            {/* Tool selection */}
            <div className="flex justify-center gap-3 mb-6 flex-wrap">
              {tools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    activeTool === tool.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                  }`}
                >
                  <span className="text-lg">{tool.icon}</span>
                  <span className="text-sm font-medium">{tool.name}</span>
                  <Kbd>{tool.key}</Kbd>
                </button>
              ))}
            </div>
            
            {/* Canvas */}
            <div 
              onClick={handleCanvasClick}
              className="relative bg-white rounded-2xl border-2 border-neutral-200 shadow-lg overflow-hidden h-[400px] cursor-crosshair bg-gradient-to-br from-slate-50 to-slate-100"
            >
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-neutral-100 rounded-lg px-3 py-1.5 z-10">
                <img src={icon} alt="" className="h-4 w-4 rounded-[20%]" />
                <span className="text-xs font-medium text-neutral-600">macdraw demo</span>
              </div>
              
              {/* Render shapes */}
              {demoShapes.map((shape) => (
                <div
                  key={shape.id}
                  className="absolute pointer-events-none transition-all duration-300"
                  style={{
                    left: shape.x,
                    top: shape.y,
                    color: shape.color,
                  }}
                >
                  {shape.type === 'rect' && (
                    <div className="w-16 h-12 border-4 rounded-lg" style={{ borderColor: shape.color }} />
                  )}
                  {shape.type === 'ellipse' && (
                    <div className="w-16 h-12 border-4 rounded-full" style={{ borderColor: shape.color }} />
                  )}
                  {shape.type === 'arrow' && (
                    <div className="text-4xl" style={{ color: shape.color }}>→</div>
                  )}
                  {shape.type === 'text' && (
                    <div className="text-2xl font-bold" style={{ color: shape.color }}>T</div>
                  )}
                  {shape.type === 'laser' && (
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ 
                      backgroundColor: shape.color,
                      boxShadow: `0 0 20px ${shape.color}`
                    }} />
                  )}
                </div>
              ))}
              
              {/* Instructions overlay */}
              {activeTool && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-neutral-900/80 text-white px-4 py-2 rounded-lg text-sm z-10">
                  Click anywhere to add {tools.find(t => t.id === activeTool)?.name}
                </div>
              )}
              
              {!activeTool && demoShapes.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">👆</div>
                    <p className="text-neutral-500">Select a tool above to start drawing</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Reset button */}
            {demoShapes.length > 0 && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={resetDemo}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition"
                >
                  <RotateCw className="h-4 w-4" />
                  Reset demo
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Reveal({
  d,
  className = "",
  children,
}: {
  d?: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`reveal ${className}`}
      style={d != null ? ({ "--d": `${d}ms` } as React.CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}

function Github({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[24px] items-center justify-center rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 font-mono text-[11px] font-semibold text-neutral-600 shadow-[0_1px_0_#e5e7eb]">
      {children}
    </kbd>
  );
}

function CountUp({
  value,
  decimals = 0,
  suffix = "",
}: {
  value: number;
  decimals?: number;
  suffix?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const dur = 900;
        const tick = (t: number) => {
          const p = Math.min((t - start) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = `${(value * eased).toFixed(decimals)}${suffix}`;
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, decimals, suffix]);
  return <span ref={ref}>0{suffix}</span>;
}

function spotlight(e: React.MouseEvent<HTMLElement>) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
}

export function App() {
  const DMG = "https://github.com/aadityakumarsah/macdraw/releases/download/v1.9.0/macdraw-1.9.0.dmg";
  const cmd = `curl -L -o ~/Downloads/macdraw.dmg ${DMG} && open ~/Downloads/macdraw.dmg`;
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [downloads, setDownloads] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/downloads")
      .then(r => (r.ok ? r.json() : null))
      .then((d: { total?: number } | null) => {
        if (d?.total) setDownloads(d.total);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="relative">
      {/* ---- nav ---- */}
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-neutral-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-6">
          <a href="#" className="flex items-center gap-2.5">
            <img src={icon} alt="" className="h-7 w-7 rounded-[22%] object-cover" />
            <span className="text-[15px] font-bold tracking-tight text-neutral-900">macdraw</span>
          </a>
          <div className="hidden items-center gap-6 text-sm font-medium text-neutral-600 sm:flex">
            <a href="#features" className="transition hover:text-neutral-900">Features</a>
            <a href="#install" className="transition hover:text-neutral-900">Install</a>
            <a href="#shortcuts" className="transition hover:text-neutral-900">Shortcuts</a>
            <a href="#faq" className="transition hover:text-neutral-900">FAQ</a>
            <a
              href="https://github.com/aadityakumarsah/macdraw"
              className="transition hover:text-neutral-900"
            >
              GitHub
            </a>
          </div>
          <a
            href="#install"
            className="flex items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-neutral-700"
          >
            Get macdraw
          </a>
        </div>
      </nav>

      {/* ---- hero ---- */}
      <header className="flex w-full flex-col items-center px-6 pt-32 pb-16 text-center sm:pt-36">
        <Reveal>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-600 shadow-sm">
            <Sparkles className="h-3 w-3 text-indigo-500" /> Free for macOS 13+
          </span>
          <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600 shadow-sm">
            <Move className="h-3 w-3" /> New: Infinite canvas
          </span>
        </Reveal>
        <Reveal d={80}>
          <img
            src={icon}
            alt="macdraw icon"
            className="animate-float mt-8 h-24 w-24 rounded-[24%] object-cover shadow-[0_10px_40px_-12px_rgba(0,0,0,0.35)] sm:h-28 sm:w-28"
          />
        </Reveal>
        <Reveal d={160}>
          <h1 className="mt-7 text-5xl font-extrabold tracking-tight text-neutral-900 sm:text-7xl">
            Your screen,{" "}
            <span className="shimmer-text bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              your canvas.
            </span>
          </h1>
        </Reveal>
        <Reveal d={240}>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-600 sm:text-xl">
            Draw, annotate and laser-point directly over any app — in seconds. No setup, no account,
            nothing to learn.
          </p>
        </Reveal>
        <Reveal d={320}>
          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
            <button
              onClick={copy}
              className="group flex items-center gap-2.5 rounded-full bg-neutral-900 px-7 py-3.5 text-base font-semibold text-white shadow-[0_8px_30px_-10px_rgba(0,0,0,0.5)] transition hover:-translate-y-0.5 hover:bg-neutral-700"
            >
              {copied ? (
                <>
                  <Check className="h-5 w-5 text-green-400" /> Copied — paste in Terminal
                </>
              ) : (
                <>
                  <Terminal className="h-5 w-5 transition group-hover:translate-y-0.5" />
                  Copy install command
                </>
              )}
            </button>
            <a
              href="#install"
              className="flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-6 py-3.5 text-base font-semibold text-neutral-700 shadow-sm transition hover:-translate-y-0.5 hover:border-neutral-400 hover:text-neutral-900"
            >
              See install steps
              <ArrowDown className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
        <Reveal d={400}>
          <p className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-neutral-500">
            <span className="flex items-center gap-1.5">
              <Apple className="h-4 w-4" /> macOS 13+
            </span>
            <span className="flex items-center gap-1.5">
              <ArrowDown className="h-3.5 w-3.5" /> ~1 MB
            </span>
            <span className="flex items-center gap-1.5">
              <ArrowDown className="h-3.5 w-3.5" />{" "}
              {downloads === null ? "…" : <CountUp value={downloads} />} downloads
            </span>
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> 100% free, no account
            </span>
          </p>
        </Reveal>

        {/* ---- demo video with Mac frame ---- */}
        <Reveal d={600} className="w-full">
          <div className="relative mx-auto mt-16 max-w-5xl">
            <div className="mac-frame">
              <div className="window-buttons">
                <div className="window-button close"></div>
                <div className="window-button minimize"></div>
                <div className="window-button maximize"></div>
              </div>
              <div className="mac-screen">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full h-full object-cover"
                  src="https://res.cloudinary.com/dye2aeml8/video/upload/v1786801047/macdraw/demo-video.mov"
                />
              </div>
              <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-neutral-900/85 px-3 py-1.5 shadow-lg backdrop-blur">
                <kbd className="rounded-md border border-white/25 bg-white/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">⌃</kbd>
                <span className="text-[11px] text-neutral-400">+</span>
                <kbd className="rounded-md border border-white/25 bg-white/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">⌥</kbd>
                <span className="ml-1 text-xs font-semibold text-white">start</span>
              </div>
              <div className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-neutral-900/85 px-3 py-1.5 shadow-lg backdrop-blur">
                <kbd className="rounded-md border border-white/25 bg-white/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">⌃</kbd>
                <span className="text-[11px] text-neutral-400">+</span>
                <kbd className="rounded-md border border-white/25 bg-white/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-white">⌥</kbd>
                <span className="ml-1 text-xs font-semibold text-white">end</span>
              </div>
            </div>
          </div>
        </Reveal>
      </header>

      {/* ---- marquee ---- */}
      <section className="w-full border-t border-neutral-200 bg-neutral-50/70 py-8">
        <p className="mb-5 text-center text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">
          Draws over everything you use
        </p>
        <div className="marquee">
          <div className="marquee-track">
            {[...apps, ...apps].map((a, i) => (
              <span
                key={i}
                className="flex items-center gap-2 whitespace-nowrap pr-10 text-sm font-medium text-neutral-400"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-300" /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---- stats ---- */}
      <section className="w-full border-b border-neutral-200">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 px-6 py-12 sm:grid-cols-4">
          {[
            { v: 17, d: 0, s: "", l: "one-key tools" },
            { v: 0.3, d: 1, s: "s", l: "autosave" },
            { v: 1, d: 0, s: " MB", l: "install size" },
            { v: 0, d: 0, s: "", l: "forever, no account" },
          ].map(({ v, d, s, l }) => (
            <div key={l} className="text-center">
              <div className="text-3xl font-extrabold tracking-tight text-neutral-900">
                {v === 0 && s === "" ? (
                  <>$0</>
                ) : (
                  <CountUp value={v} decimals={d} suffix={s} />
                )}
              </div>
              <div className="mt-1 text-sm text-neutral-500">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- install ---- */}
      <section id="install" className="w-full scroll-mt-16 py-20">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Install in 20 seconds
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
              Two ways in — both skip Gatekeeper entirely, so the app opens with a plain
              double-click.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            <Reveal d={100} className="h-full">
              <div
                onMouseMove={spotlight}
                className="spotlight flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition hover:-translate-y-1 hover:border-neutral-300 hover:shadow-[0_12px_30px_-12px_rgba(0,0,0,0.15)]"
              >
                <div className="mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-900 text-sm font-bold text-white">
                  1
                </div>
                <h3 className="mb-1.5 font-semibold text-neutral-900">Terminal — no prompts</h3>
                <p className="mb-4 text-sm leading-relaxed text-neutral-600">
                  Paste this into Terminal and press return. No Gatekeeper prompt — macdraw opens
                  with a plain double-click.
                </p>
                <div className="mt-auto rounded-xl bg-neutral-900 p-3.5 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.6)]">
                  <div className="mb-2.5 flex items-center justify-between">
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/80" />
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500/80" />
                    </div>
                    <button
                      onClick={copy}
                      className="flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[11px] font-semibold text-neutral-200 transition hover:bg-white/20"
                    >
                      {copied ? (
                        <Check className="h-3 w-3 text-green-400" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <code className="block overflow-x-auto whitespace-nowrap font-mono text-[11.5px] leading-relaxed">
                    <span className="select-none text-neutral-500">$ </span>
                    <span className="text-neutral-100">curl -L -o ~/Downloads/macdraw.dmg </span>
                    <span className="text-sky-300">{DMG}</span>
                    <span className="text-neutral-100"> && open ~/Downloads/macdraw.dmg</span>
                    <div className="mt-2 text-[11px] text-neutral-400">macdraw.dmg · 3.6 MB</div>
                  </code>
                </div>
                <p className="mt-3 text-[11px] text-neutral-400">
                  Apple Silicon &amp; Intel · macOS 13+ · no Homebrew needed
                </p>
              </div>
            </Reveal>
            <Reveal d={200} className="h-full">
              <div
                onMouseMove={spotlight}
                className="spotlight flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition hover:-translate-y-1 hover:border-neutral-300 hover:shadow-[0_12px_30px_-12px_rgba(0,0,0,0.15)]"
              >
                <div className="mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-neutral-900 text-sm font-bold text-white">
                  2
                </div>
                <h3 className="mb-4 font-semibold text-neutral-900">AirDrop — no prompts</h3>
                <div className="relative mx-auto mb-5 flex h-20 w-20 items-center justify-center">
                  <span className="radar-ring" />
                  <span className="radar-ring" />
                  <span className="radar-ring" />
                  <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-indigo-100 bg-indigo-50">
                    <img src={icon} alt="" className="h-8 w-8 rounded-[24%] object-cover" />
                  </span>
                </div>
                <ol className="mt-auto space-y-2.5">
                  {[
                    ["Finder → AirDrop", "set it to “Everyone” or “Contacts Only”"],
                    ["Drag the DMG", "onto the other Mac — it arrives untouched"],
                    ["Double-click", "and drag macdraw to Applications"],
                  ].map(([t, d], i) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-[11px] font-bold text-indigo-600">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-neutral-600">
                        <strong className="font-semibold text-neutral-900">{t}</strong>
                        <span className="text-neutral-500"> — {d}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-sm leading-relaxed text-neutral-600">
                  AirDrop skips Gatekeeper entirely — the easiest way to share macdraw with friends.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---- how to use ---- */}
      <section id="how-to" className="w-full scroll-mt-16 pb-20">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              How to use it
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
              Three moves, zero learning. Watch the keys glow — that's all it takes.
            </p>
          </Reveal>
          <Reveal d={100}>
            <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center">
              <div className="rounded-2xl border border-neutral-200 bg-gradient-to-b from-neutral-50 to-white p-6">
                <div className="howto-screen relative mx-auto h-44 w-full max-w-80 overflow-hidden rounded-xl border border-neutral-200 bg-white">
                  <div className="absolute inset-0 flex items-center justify-center text-neutral-300">
                    <svg width="220" height="120" viewBox="0 0 220 120" fill="none" className="drop-shadow-sm">
                      <rect x="12" y="10" width="88" height="60" rx="6" stroke="#c7d2fe" strokeWidth="2" opacity="0.9" />
                      <rect x="12" y="10" width="88" height="60" rx="6" fill="#eef2ff" fillOpacity="0.7" />
                      <circle cx="152" cy="76" r="22" stroke="#9ca3af" strokeWidth="2" opacity="0.8" />
                      <rect x="24" y="24" width="62" height="6" rx="3" fill="#a5b4fc" />
                      <rect x="24" y="38" width="46" height="6" rx="3" fill="#e5e7eb" />
                      <line x1="150" y1="16" x2="196" y2="32" stroke="#f0abfc" strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
                      <path d="M120 104 Q 140 92 158 100 T 196 94" stroke="#fda4af" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.8" />
                      <g className="howto-laser">
                        <rect x="108" y="14" width="3.5" height="3.5" rx="1" fill="#f43f5e" />
                      </g>
                      <g className="howto-draw">
                        <rect x="96" y="12" width="56" height="34" rx="5" stroke="#6366f1" strokeWidth="2" strokeDasharray="5 3" opacity="0.9" />
                        <text x="118" y="34" textAnchor="middle" fontSize="11" fontWeight="700" fill="#6366f1">draw</text>
                      </g>
                    </svg>
                  </div>
                  <div className="howto-overlay absolute inset-x-0 top-0 h-full rounded-xl border border-indigo-200 bg-white/95 shadow-[0_16px_40px_-16px_rgba(79,70,229,0.4)]">
                    <div className="flex h-full items-center justify-center">
                      <svg width="220" height="120" viewBox="0 0 220 120" fill="none" className="drop-shadow-sm">
                        <rect x="12" y="10" width="88" height="60" rx="6" stroke="#c7d2fe" strokeWidth="2" />
                        <rect x="12" y="10" width="88" height="60" rx="6" fill="#eef2ff" fillOpacity="0.7" />
                        <circle cx="152" cy="76" r="22" stroke="#9ca3af" strokeWidth="2" />
                        <rect x="24" y="24" width="62" height="6" rx="3" fill="#a5b4fc" />
                        <rect x="24" y="38" width="46" height="6" rx="3" fill="#e5e7eb" />
                        <line x1="150" y1="16" x2="196" y2="32" stroke="#f0abfc" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
                        <path d="M120 104 Q 140 92 158 100 T 196 94" stroke="#fda4af" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6" />
                        <g className="howto-laser">
                          <circle cx="114" cy="24" r="1.75" fill="#f43f5e" />
                        </g>
                      </svg>
                      <span className="howto-draw absolute bottom-2 left-3 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white">
                        drawing
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-center gap-2.5">
                  <kbd className="howto-key howto-key-ctrl" aria-label="Control key">⌃</kbd>
                  <span className="text-sm text-neutral-400">+</span>
                  <kbd className="howto-key howto-key-opt" aria-label="Option key">⌥</kbd>
                  <span className="mx-1 h-px w-6 bg-neutral-200" />
                  <svg width="34" height="14" viewBox="0 0 34 14" fill="none" className="text-neutral-300">
                    <path d="M2 7h28m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <kbd className="howto-key howto-key-esc text-xs" aria-label="Escape key">esc</kbd>
                </div>
                <p className="mt-5 text-center text-sm text-neutral-500">
                  Hold <b className="text-neutral-800">⌃⌥</b> to open the canvas — press <b className="text-neutral-800">Esc</b> to close it. Everything is saved. Re-press <b className="text-neutral-800">⌃⌥</b> to open it again.
                </p>
              </div>
              <div className="flex flex-col gap-4">
                {[
                  {
                    n: "1",
                    t: "Hold ⌃⌥",
                    d: "The canvas drops in over any app you're using — full screen, right where you're looking.",
                  },
                  {
                    n: "2",
                    t: "Draw",
                    d: "Shapes, text, images, glowing laser. The toolbar follows your cursor, so nothing gets in the way.",
                  },
                  {
                    n: "3",
                    t: "Press Esc",
                    d: "The overlay closes and your drawing is saved automatically — reopen it the same way anytime.",
                  },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
                      {s.n}
                    </span>
                    <div>
                      <h3 className="font-semibold text-neutral-900">{s.t}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-600">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---- features ---- */}
      <section id="features" className="w-full scroll-mt-16 py-20">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Everything a whiteboard has — over any app
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
              Built for presentations, meetings, teaching and live streaming. Press ⌃⌥ and your
              screen becomes a canvas.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <Reveal key={f.title} d={i * 60} className="h-full">
                <div
                  onMouseMove={spotlight}
                  className="spotlight group h-full rounded-2xl border border-neutral-200 bg-white p-6 transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_12px_30px_-12px_rgba(79,70,229,0.25)]"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition group-hover:scale-110 group-hover:bg-indigo-600 group-hover:text-white">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1.5 font-semibold text-neutral-900">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-neutral-600">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* ---- why macdraw is different ---- */}
          <Reveal>
            <div className="mt-20">
              <h3 className="text-center text-2xl font-extrabold tracking-tight sm:text-3xl">
                Why it's different
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
                Not another whiteboard app — a canvas that lives on top of everything you already
                use.
              </p>
            </div>
          </Reveal>
          <Reveal d={100}>
            <div className="mt-8 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="px-5 py-3.5 font-semibold text-neutral-900">Why macdraw wins</th>
                    <th className="px-5 py-3.5 font-semibold text-neutral-900">macdraw</th>
                    <th className="px-5 py-3.5 font-semibold text-neutral-500">
                      Other whiteboards
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Draws over any app on your screen", true, false],
                    ["No window, no tab — it's always on top", true, false],
                    ["Every tool is one keystroke away", true, false],
                    ["1 MB install, opens instantly", true, false],
                    ["Works offline with no account", true, false],
                    ["Free forever, no subscriptions", true, false],
                    ["Autosaves every drawing", true, true],
                    ["Export PNG / SVG / PDF", true, true],
                  ].map(([label, mac, others]) => (
                    <tr key={label as string} className="border-b border-neutral-100 last:border-0">
                      <td className="px-5 py-3 text-neutral-700">{label as string}</td>
                      <td className="px-5 py-3">
                        <Check className="h-4 w-4 text-emerald-500" />
                      </td>
                      <td className="px-5 py-3">
                        {others ? (
                          <Check className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <X className="h-4 w-4 text-neutral-300" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-center text-xs text-neutral-400">
              Compared with typical whiteboard apps — Freeform, Zoom whiteboard, Miro, Excalidraw.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---- shortcuts ---- */}
      <section id="shortcuts" className="w-full scroll-mt-16 pb-20">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Every tool, one keystroke away
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
              No menus to dig through — the toolbar remembers your last tool, and the shortcuts do
              the rest.
            </p>
          </Reveal>
          <Reveal d={100}>
            <div className="mx-auto mt-10 grid max-w-3xl gap-x-10 gap-y-3 sm:grid-cols-2">
              {shortcuts.map(([name, key]) => (
                <div
                  key={name}
                  className="flex items-center justify-between rounded-lg px-3 py-1.5 transition hover:bg-neutral-50"
                >
                  <span className="text-sm text-neutral-600">{name}</span>
                  <Kbd>{key}</Kbd>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section id="faq" className="w-full scroll-mt-16 pb-20">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Questions, answered
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-neutral-600">
              The honest answers — including why Apple gives macdraw the side-eye.
            </p>
          </Reveal>
          <div className="mt-10 space-y-3">
            {faqs.map(([q, a], i) => (
              <Reveal key={q} d={i * 60}>
                <div className="rounded-2xl border border-neutral-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition hover:border-neutral-300">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="font-semibold text-neutral-900">{q}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-300 ${
                        openFaq === i ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${
                      openFaq === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-sm leading-relaxed text-neutral-600">{a}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---- CTA band ---- */}
      <section className="w-full px-6 pb-20">
        <Reveal>
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-neutral-900 px-8 py-16 text-center">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-indigo-500/25 blur-3xl" />
            <h2 className="relative text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Ready to draw over anything?
            </h2>
            <p className="relative mx-auto mt-3 max-w-xl text-neutral-400">
              Get macdraw, press ⌃⌥, and your screen becomes a canvas. 1 MB, free forever.
            </p>
            <div className="relative mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                onClick={copy}
                className="flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-neutral-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-neutral-200"
              >
                {copied ? (
                  <>
                    <Check className="h-5 w-5 text-green-600" /> Copied — paste in Terminal
                  </>
                ) : (
                  <>
                    <Terminal className="h-5 w-5" /> Copy install command
                  </>
                )}
              </button>
              <a
                href="#install"
                className="rounded-full border border-white/25 px-6 py-3.5 text-base font-semibold text-white transition hover:border-white/50 hover:bg-white/10"
              >
                Install steps
              </a>
            </div>
            <p className="relative mt-5 text-xs text-neutral-500">
              macOS 13+ · Apple Silicon & Intel · no account
            </p>
          </div>
        </Reveal>
      </section>

      {/* ---- footer ---- */}
      <footer className="w-full border-t border-neutral-200 py-10">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img src={icon} alt="" className="h-6 w-6 rounded-[22%] object-cover" />
            <span className="text-sm font-bold text-neutral-900">macdraw</span>
            <span className="text-sm text-neutral-500">
              Built with Swift &amp; AppKit · Hosted on Bun
            </span>
          </div>
          <div className="flex items-center gap-5 text-sm text-neutral-500">
            <a
              href="https://github.com/aadityakumarsah/macdraw"
              className="flex items-center gap-1.5 transition hover:text-neutral-900"
            >
              <Github className="h-4 w-4" /> macdraw
            </a>
            <a
              href="https://github.com/aadityakumarsah/macdraw-site"
              className="flex items-center gap-1.5 transition hover:text-neutral-900"
            >
              <Github className="h-4 w-4" /> this site
            </a>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-neutral-400">
          © 2026 macdraw · Free forever, no account needed
        </p>
      </footer>
    </div>
  );
}

export default App;
